import CloseIcon from '@mui/icons-material/Close';
import { Modal, Box, IconButton, styled } from '@mui/material';
import React, { useState, Children, useMemo, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

const MAX_HEIGHT = 350;

const CarouselWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  width: '100%',
  gap: theme.spacing(1),
}));

const CarouselContainer = styled('div')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: `${MAX_HEIGHT}px`,
  touchAction: 'pan-y', // Allow vertical scrolling but enable horizontal swipe detection
  userSelect: 'none', // Prevent text selection during swipe
  WebkitUserSelect: 'none',
  outline: 'none',
  
  '&:focus-visible': {
    outline: '2px solid #0FCB8C',
    outlineOffset: '2px',
  },
});

const CarouselContent = styled('div')({
  display: 'flex',
  width: '100%',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  // Fade transition for image switching
  '& > *': {
    animation: 'fadeIn 0.3s ease-in-out',
  },
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
});

const NavigationButton = styled('button')({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'rgba(10, 50, 53, 0.8)', // dark-teal-100
  color: '#F5F5DC', // cream-100
  border: 'none',
  borderRadius: '50%',
  width: '30px',
  height: '30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '16px',
  fontWeight: 500,
  opacity: 0.6,
  cursor: 'pointer',
  '&:hover': {
    background: '#0FCB8C', // green-100
    color: '#F5F5DC', // cream-100
  },
  '&:disabled': {
    background: 'rgba(10, 50, 53, 0.1)', // dark-teal-10
    cursor: 'not-allowed',
  },
});

const PrevButton = styled(NavigationButton)(({ theme }) => ({
  left: theme.spacing(0.5),
}));

const NextButton = styled(NavigationButton)(({ theme }) => ({
  right: theme.spacing(0.5),
}));

const DotsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
}));

const Dot = styled('div')<{ active: boolean }>(({ active }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: active ? '#0FCB8C' : 'transparent',
  border: `2px solid ${active ? '#0FCB8C' : '#F5F5DC'}`,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.2)',
  },
}));

const InlineFiguresCarouselWidgetInner: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const figures = useMemo(() => Children.toArray(children), [children]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [captionAnnouncement, setCaptionAnnouncement] = useState<string>('');
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Touch/swipe support
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Extract caption from current figure for announcement
  const getCurrentCaption = (index: number): string => {
    const figure = figures[index];
    if (figure && typeof figure === 'object' && 'props' in figure) {
      const props = (figure as any).props;
      const name = props?.name || '';
      const caption = props?.caption || '';
      
      // Check if caption already starts with the name
      const captionStartsWithName = caption && caption.toLowerCase().startsWith(name.toLowerCase());
      
      if (captionStartsWithName) {
        return caption;
      } else if (caption) {
        return `${name}: ${caption}`;
      } else {
        return name;
      }
    }
    return `Figure ${index + 1}`;
  };

  // Announce caption when index changes (but only after user interaction)
  useEffect(() => {
    if (hasInteracted) {
      const announcement = getCurrentCaption(currentIndex);
      // Clear first to ensure change is detected
      setCaptionAnnouncement('');
      const timer = setTimeout(() => {
        setCaptionAnnouncement(announcement);
      }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, hasInteracted]);

  const goToPrevious = () => {
    setHasInteracted(true);
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : figures.length - 1));
    // Track carousel left button click
    // @ts-ignore
    if (window.goatcounter) {
      // @ts-ignore
      window.goatcounter.count({
        path: 'carousel-previous-click',
        title: 'Carousel Previous Button',
        event: true,
      });
    }
  };

  const goToNext = () => {
    setHasInteracted(true);
    setCurrentIndex((prev) => (prev < figures.length - 1 ? prev + 1 : 0));
    // Track carousel right button click
    // @ts-ignore
    if (window.goatcounter) {
      // @ts-ignore
      window.goatcounter.count({
        path: 'carousel-next-click',
        title: 'Carousel Next Button',
        event: true,
      });
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null); // Reset touch end
    if (e.targetTouches[0]) {
      setTouchStart(e.targetTouches[0].clientX);
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (e.targetTouches[0]) {
      setTouchEnd(e.targetTouches[0].clientX);
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Keyboard navigation support
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goToPrevious();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goToNext();
    }
  };

  if (figures.length === 0) return null;

  return (
    <CarouselWrapper>
      <CarouselContainer
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="region"
        aria-label={`Image carousel, ${figures.length} images. Currently showing: ${getCurrentCaption(currentIndex)}`}
        onFocus={(e) => {
          // Only announce on initial focus if user explicitly focuses the carousel
          // Don't auto-announce on page load
          if (e.target === e.currentTarget && hasInteracted) {
            const announcement = getCurrentCaption(currentIndex);
            setCaptionAnnouncement('');
            setTimeout(() => setCaptionAnnouncement(announcement), 50);
          }
        }}
      >
        <CarouselContent key={currentIndex}>
          {figures[currentIndex]}
        </CarouselContent>
        {figures.length > 1 && (
          <>
            <PrevButton 
              onClick={goToPrevious} 
              title="Previous image"
              aria-label="Previous image"
            >
              ‹
            </PrevButton>
            <NextButton 
              onClick={goToNext} 
              title="Next image"
              aria-label="Next image"
            >
              ›
            </NextButton>
          </>
        )}
      </CarouselContainer>
      {/* Screen reader announcement for caption changes */}
      <div 
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        aria-live="assertive" 
        aria-atomic="true"
        role="status"
      >
        {captionAnnouncement}
      </div>
      {figures.length > 1 && (
        <DotsContainer role="tablist" aria-label="Carousel navigation">
          {figures.map((_, index) => (
            <Dot
              key={index}
              active={index === currentIndex}
              onClick={() => {
                setHasInteracted(true);
                setCurrentIndex(index);
              }}
              role="tab"
              aria-selected={index === currentIndex}
              aria-label={`Go to image ${index + 1} of ${figures.length}`}
              title={`Image ${index + 1} of ${figures.length}`}
            />
          ))}
        </DotsContainer>
      )}
    </CarouselWrapper>
  );
};

export const InlineFiguresCarouselWidget: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <ErrorBoundary fallback={<></>}>
      <InlineFiguresCarouselWidgetInner>
        {children}
      </InlineFiguresCarouselWidgetInner>
    </ErrorBoundary>
  );
};

const FigureCarouselItem = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: 'auto',
  height: `${MAX_HEIGHT}px`,
  gap: '8px',
});

const FigureInfoContainer = styled('div')({
  fontSize: '0.75rem',
  color: '#FFFFFF',
  textAlign: 'center',
  width: '100%',
  maxWidth: '500px',
  lineHeight: 1.4,
  paddingTop: '4px',
});

const FigureCarouselItemImageContainer = styled('div')({
  width: 'auto',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  minHeight: 0, // Important for flexbox to allow shrinking
});

const FigureNameSpan = styled('span')({
  fontWeight: 'bold',
  color: '#0FCB8C',
});

const ModalBox = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none',
});

const ModalImage = styled('img')({
  maxWidth: '90vw',
  maxHeight: '90vh',
  objectFit: 'contain',
});

const FigureName = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(2),
  left: '50%',
  transform: 'translateX(-50%)',
  color: 'white',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  borderRadius: theme.spacing(0.5),
  fontSize: '1rem',
  fontWeight: 'bold',
}));

export const InlineFigureWidget = ({
  url,
  name,
  caption,
}: {
  url: string;
  name: string;
  caption?: string;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleImageClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Check if caption already starts with the figure name
  const captionStartsWithName = caption && caption.toLowerCase().startsWith(name.toLowerCase());
  
  return (
    <>
      <FigureCarouselItem>
        <FigureInfoContainer>
          {captionStartsWithName ? (
            // If caption includes the name, just show the caption
            <span>{caption}</span>
          ) : (
            // Otherwise, show name: caption
            <>
              <FigureNameSpan>{name}</FigureNameSpan>
              {caption && <span>: {caption}</span>}
            </>
          )}
        </FigureInfoContainer>
        <FigureCarouselItemImageContainer onClick={handleImageClick}>
          <img
            src={url}
            alt={name}
            loading="lazy"
            decoding="async"
            style={{ objectFit: 'contain', height: '100%', width: '100%' }}
          />
        </FigureCarouselItemImageContainer>
      </FigureCarouselItem>

      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <ModalBox>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0)',
            }}
          >
            <CloseIcon />
          </IconButton>
          <ModalImage src={url} alt={name} />
          <FigureName>
            {caption && (
              <>
                <br />
                <span style={{ fontWeight: 'normal', fontSize: '0.9rem' }}>{caption}</span>
              </>
            )}
          </FigureName>
        </ModalBox>
      </Modal>
    </>
  );
};